import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import SideNavbar from '../../components/SideNavbar';
import { collection, doc, getDoc, getDocs, getCountFromServer, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAppSelector } from '../../redux/store';

interface LeadStatus {
    status: string;
    color: string;
    priority: number;
}

interface LeadStage {
    stage: string;
    color: string;
    priority: number;
}

interface LeadSource {
    source: string;
}

interface DashboardCounts {
    today: number;
    week: number;
    month: number;
    total: number;
}

const StaffModule = () => {
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const { currentUser, userData, logout } = useAuth();
    const [companyId, setCompanyId] = useState<string>('');
    const [leadStatuses, setLeadStatuses] = useState<{ [key: string]: LeadStatus }>({});
    const [leadStages, setLeadStages] = useState<{ [key: string]: LeadStage }>({});
    const [leadSources, setLeadSources] = useState<{ [key: string]: LeadSource }>({});
    const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts>({
        today: 0,
        week: 0,
        month: 0,
        total: 0
    });

    useEffect(() => {
        const fetchUserCompany = async () => {
            if (!currentUser) return;

            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data();
            if (userData?.companyId) {
                setCompanyId(userData.companyId);
            }
        };

        fetchUserCompany();
    }, [currentUser]);

    useEffect(() => {
        if (!companyId || !currentUser) return;

        const fetchStatuses = async () => {
            const statusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`));
            const statusesData: { [key: string]: LeadStatus } = {};
            statusesSnapshot.forEach(doc => {
                statusesData[doc.id] = doc.data() as LeadStatus;
            });
            setLeadStatuses(statusesData);
        };

        const fetchStages = async () => {
            const stagesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`));
            const stagesData: { [key: string]: LeadStage } = {};
            stagesSnapshot.forEach(doc => {
                stagesData[doc.id] = doc.data() as LeadStage;
            });
            setLeadStages(stagesData);
        };

        const fetchSources = async () => {
            const sourcesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`));
            const sourcesData: { [key: string]: LeadSource } = {};
            sourcesSnapshot.forEach(doc => {
                sourcesData[doc.id] = doc.data() as LeadSource;
            });
            setLeadSources(sourcesData);
        };

        fetchStatuses();
        fetchStages();
        fetchSources();
    }, [companyId, currentUser]);

    useEffect(() => {
        const fetchCounts = async () => {
            if (!companyId || !currentUser || !leadStatuses || !leadStages || !leadSources) return;

            // First get total active leads for the user
            const qTotalActive = query(
                collection(db, `companies/${companyId}/leads`),
                where('assignedTo', '==', currentUser.uid),
                where('isActive', '==', true)
            );
            const totalActiveSnapshot = await getCountFromServer(qTotalActive);
            const totalActiveLeads = totalActiveSnapshot.data().count;

            // Fetch status counts
            const statusCountsData: { [key: string]: number } = { other: 0 };
            let totalStatusCounted = 0;

            for (const statusId of Object.keys(leadStatuses)) {
                const q = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('assignedTo', '==', currentUser.uid),
                    where('isActive', '==', true),
                    where('statusId', '==', statusId)
                );
                const snapshot = await getCountFromServer(q);
                const count = snapshot.data().count;
                statusCountsData[statusId] = count;
                totalStatusCounted += count;
            }

            // Calculate others by subtracting total counted from total active
            statusCountsData.other = totalActiveLeads - totalStatusCounted;
            // setStatusCounts(statusCountsData);

            // Fetch stage counts
            const stageCountsData: { [key: string]: number } = { other: 0 };
            let totalStageCounted = 0;

            for (const stageId of Object.keys(leadStages)) {
                const q = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('assignedTo', '==', currentUser.uid),
                    where('isActive', '==', true),
                    where('stageId', '==', stageId)
                );
                const snapshot = await getCountFromServer(q);
                const count = snapshot.data().count;
                stageCountsData[stageId] = count;
                totalStageCounted += count;
            }

            // Calculate others by subtracting total counted from total active
            stageCountsData.other = totalActiveLeads - totalStageCounted;
            // setStageCounts(stageCountsData);

            // Fetch source counts
            const targetSources = ['Meta Ads', 'Instagram', 'Advertisement', 'Sale', 'Marketing'];
            const sourceCountsData: { [key: string]: number } = { other: 0 };
            let totalSourceCounted = 0;

            // Count leads for each target source
            for (const [sourceId, sourceData] of Object.entries(leadSources)) {
                if (targetSources.includes(sourceData.source)) {
                    const q = query(
                        collection(db, `companies/${companyId}/leads`),
                        where('assignedTo', '==', currentUser.uid),
                        where('isActive', '==', true),
                        where('sourceId', '==', sourceId)
                    );
                    const snapshot = await getCountFromServer(q);
                    const count = snapshot.data().count;
                    sourceCountsData[sourceId] = count;
                    totalSourceCounted += count;
                }
            }

            // Calculate others by subtracting total counted from total active
            sourceCountsData.other = totalActiveLeads - totalSourceCounted;
            // setSourceCounts(sourceCountsData);

            // Fetch dashboard counts
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            const qToday = query(
                collection(db, `companies/${companyId}/leads`),
                where('assignedTo', '==', currentUser.uid),
                where('isActive', '==', true),
                where('createdAt', '>=', Timestamp.fromDate(startOfDay))
            );
            const qWeek = query(
                collection(db, `companies/${companyId}/leads`),
                where('assignedTo', '==', currentUser.uid),
                where('isActive', '==', true),
                where('createdAt', '>=', Timestamp.fromDate(startOfWeek))
            );
            const qMonth = query(
                collection(db, `companies/${companyId}/leads`),
                where('assignedTo', '==', currentUser.uid),
                where('isActive', '==', true),
                where('createdAt', '>=', Timestamp.fromDate(startOfMonth))
            );

            const [todayCount, weekCount, monthCount] = await Promise.all([
                getCountFromServer(qToday),
                getCountFromServer(qWeek),
                getCountFromServer(qMonth)
            ]);

            setDashboardCounts({
                today: todayCount.data().count,
                week: weekCount.data().count,
                month: monthCount.data().count,
                total: totalActiveLeads
            });
        };

        fetchCounts();
    }, [companyId, currentUser, leadStatuses, leadStages, leadSources]);

    return (
        <div>
            <div className="bg-white p-6 rounded-3xl shadow-md w-full h-auto">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 basis-32 flex flex-col justify-center items-center bg-gray-100 h-20 rounded-3xl min-w-[120px] relative">
                        <div className="text-center">
                            <h3 className="font-Montserrat-gray-900 text-xl font-medium ">{dashboardCounts.today}</h3>
                            <p className="font-Montserrat-gray-900 text-xs font-normal">Today's Leads</p>
                        </div>
                    </div>

                    <div className="flex-1 basis-32 flex flex-col justify-center items-center bg-gray-100 h-20 rounded-3xl min-w-[120px]">
                        <h3 className="font-Montserrat-gray-900 text-xl font-medium ">{dashboardCounts.week}</h3>
                        <p className="font-Montserrat-gray-900 text-xs font-normal">This Week</p>
                    </div>
                    <div className="flex-1 basis-32 flex flex-col justify-center items-center bg-gray-100 h-20 rounded-3xl min-w-[120px]">
                        <h3 className="font-Montserrat-gray-900 text-xl font-medium ">{dashboardCounts.month}</h3>
                        <p className="font-Montserrat-gray-900 text-xs font-normal">This Month</p>
                    </div>
                    <div className="flex-1 basis-32 flex flex-col justify-center items-center bg-gray-100 h-20 rounded-3xl min-w-[120px]">
                        <h3 className="font-Montserrat-gray-900 text-xl font-medium ">{dashboardCounts.total}</h3>
                        <p className="font-Montserrat-gray-900 text-xs font-normal">Total Leads</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffModule;